import {
  Survey,
  SearchResponse,
  SurveyById,
  SurveyMetaData,
  SaveFormModaData,
  SurveyHHFilters,
  FormFetchData,
  CommonGetOptions,
} from "interfaces/dataInterfaces"
import { blobToDownload, transformResponse } from "utils/helpers"

import { householdApi } from "./householdApi"

export const surveyHousehold = householdApi.injectEndpoints({
  endpoints: builder => ({
    getSurveyHHList: builder.query<
      SearchResponse<Survey>,
      { query: { skip: number; top: number; adm0Code: number; search: string }; body: SurveyHHFilters }
    >({
      query: ({ query, body }) => ({
        url: `Survey/HouseholdSurveys`,
        method: "POST",
        params: query,
        body,
      }),
      providesTags: ["SurveysHousehold"],
    }),

    saveSurveyHH: builder.mutation<{ isSuccess: boolean; resultMessage: string; surveyId: number }, FormData>({
      query: body => ({
        url: "Survey/SaveCsv",
        method: "POST",
        body,
      }),
      invalidatesTags: ["SurveysHousehold"],
    }),

    getSurveyHH: builder.query<SurveyById, number>({
      query: id => ({
        url: `Survey/Get/${id}`,
      }),
      providesTags: ["SurveyHousehold"],
    }),

    deleteSurveyHH: builder.mutation<void, number>({
      query: id => ({
        url: `Survey/Delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SurveysHousehold"],
    }),

    downloadOriginSurveyHH: builder.mutation<void, { surveyId?: number; file?: string }>({
      query: ({ file, surveyId }) => ({
        url: "Survey/DownloadOriginSurvey",
        method: "GET",
        params: {
          surveyId,
          surveyOriginalFilename: file,
        },
        responseHandler: async (response: Response) => {
          response.ok && blobToDownload(response, file)
        },
      }),
    }),

    downloadSurveyHouseholdFile: builder.mutation<void, { dataType: string; surveyId: number; format: string }>({
      query: ({ dataType, surveyId, format }) => {
        let url = `/Admin/Srvc/Proxy?u=apiget/Household/${dataType}?surveyID=${surveyId}&xlsFormType=household`
        if (format) url += `&format=${format}`
        return {
          url,
          responseHandler: async (response: Response) => {
            const res = await transformResponse(response, format)
            return res
          },
        }
      },
    }),

    updateStatusHH: builder.mutation<void, { surveyId: number; statusId: number }>({
      query: ({ surveyId, statusId }) => ({
        url: "Survey/UpdateStatus",
        method: "PUT",
        params: { surveyId, statusId },
      }),
      invalidatesTags: ["SurveysHousehold", "SurveyHousehold"],
    }),

    updateVisibilityHH: builder.mutation<void, { surveyId: number; visibilityId: number }>({
      query: body => ({
        url: `Survey/UpdateVisibility`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["SurveysHousehold"],
    }),

    updateHouseholdDetails: builder.mutation<void, SurveyMetaData>({
      query: body => ({
        url: `Survey/PutSurveyHouseholdDetail`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["SurveysHousehold", "SurveyHousehold"],
    }),

    saveSurveyHHFetch: builder.mutation<
      { isSuccess: boolean; resultMessage: string; surveyId: number },
      SaveFormModaData
    >({
      query: body => ({
        url: "Survey/SaveFetch",
        method: "POST",
        body,
      }),
      invalidatesTags: ["SurveysHousehold"],
    }),

    updateHouseholdFrequency: builder.mutation<void, FormFetchData>({
      query: body => ({
        url: "Survey/UpdateFetchingOperationJob",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["SurveysHousehold"],
    }),

    getHHXlsForm: builder.query<CommonGetOptions[], { adm0Code: number; xlsFormSubCategoryId: number }>({
      query: query => ({
        url: `Survey/GetXlsFormsByCountryAndSubcategory`,
        params: query,
        method: "GET",
      }),
      providesTags: ["FormsByCountryAndSubcategory"],
    }),

    getAssessmentStatuses: builder.query<CommonGetOptions[], void>({
      query: () => ({
        url: `Survey/GetAssessmentStatuses`,
        method: "GET",
      }),
    }),

    getPhases: builder.query<CommonGetOptions[], void>({
      query: () => ({
        url: `Survey/GetPhases`,
        method: "GET",
      }),
    }),

    getVisibilities: builder.query<CommonGetOptions[], void>({
      query: () => ({
        url: `Survey/GetVisibilities`,
        method: "GET",
      }),
    }),
  }),
})

export const {
  useGetSurveyHHListQuery,
  useSaveSurveyHHMutation,
  useGetSurveyHHQuery,
  useDeleteSurveyHHMutation,
  useDownloadOriginSurveyHHMutation,
  useDownloadSurveyHouseholdFileMutation,
  useUpdateStatusHHMutation,
  useUpdateVisibilityHHMutation,
  useUpdateHouseholdDetailsMutation,
  useSaveSurveyHHFetchMutation,
  useUpdateHouseholdFrequencyMutation,
  useGetHHXlsFormQuery,
  useGetAssessmentStatusesQuery,
  useGetPhasesQuery,
  useGetVisibilitiesQuery,
} = surveyHousehold
